import { ModuleRegistry, createGrid, themeQuartz } from "ag-grid-community"
import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise"
import ActionsRenderer from "./components/actions_renderer"
import excelExportCallback from "./components/excel_export_callback"
import numberComparator from "./components/number_comparator"
import dateComparator from "./components/date_comparator"
import linkValueFormatter from "./components/link_value_formatter"
import LinkRenderer from "./components/link_renderer"

ModuleRegistry.registerModules([
  AllEnterpriseModule,
])

export function initializeGrid(options) {
  let eGridDiv = document.getElementsByClassName("ag-grid")[options.gridName]
  if (!eGridDiv) { return } // Exit out if there is no data to parse

  let path = ""
  if (/\?/.test(eGridDiv.dataset.path)) {
    let [uri, args] = eGridDiv.dataset.path.split("?")
    path = uri + ".json?" + args
  } else {
    path = eGridDiv.dataset.path + ".json"
  }
  let gridOptions = baseGridOptions(options, path)
  gridOptions.columnDefs = JSON.parse(eGridDiv.dataset.columnDefs)
  eGridDiv.style.setProperty("height", gridOptions.context.gridHeight)

  LicenseManager.setLicenseKey(eGridDiv.dataset.license)
  const gridApi = createGrid(eGridDiv, gridOptions)
  eGridDiv.grid = gridApi

  gridApi.gridDiv = eGridDiv
  gridApi.name = eGridDiv.id
  gridApi.dataPath = path
  gridApi.updatePath = gridOptions.context?.updatePath
  gridApi.updatedRows = []

  // resize columns in the grid to fit the available space
  if (gridOptions.context.sizeColumnsToFit) {
    gridApi.sizeColumnsToFit()
  }

  gridOptions.onModelUpdated = () => {
    if (gridOptions.context.autoSizeColumns) {
      gridApi.autoSizeColumns(gridOptions.context.autoSizeColumns)
    }

    if (gridOptions.context.autoSizeAllColumns) {
      gridApi.autoSizeAllColumns()
    }
  }

  // Set up grid buttons
  gridApi.addEventListener("filterChanged", () => {
    let clearFiltersButton = document.getElementById(`${eGridDiv.id}_clear_filters`)
    const filtersPresent = Object.keys(gridApi.getFilterModel()).length

    if (filtersPresent && filtersPresent > 0) {
      clearFiltersButton.disabled = false
    } else {
      clearFiltersButton.disabled = true
    }
  })

  gridApi.isVisible = function() {
    return gridApi.gridDiv.checkVisibility()
  }

  window.agGrids ||= []
  window.agGrids.push(gridApi)
}

export function baseGridOptions(options = {}, path = "") {
  let defaultOptions = {
    columnDefs: [],
    onGridReady: (params) => {
      fetch(path)
        .then(response => response.json())
        .then(data => {
          params.api.setGridOption("rowData", data)
        })
        .catch(error => {
          alert(`Error loading table data: ${error}`, "danger")
        })
    },
    theme: themeQuartz,
    defaultColDef: {
      enableCellChangeFlash: true, // Flash green when a cell's value is updated
      filter: "agSetColumnFilter", // Make every column use 'text' filter by default
      filterParams: {
        defaultToNothingSelected: true
      },
      menuTabs: ["generalMenuTab", "filterMenuTab"],
      minWidth: 75,
      floatingFilter: true, // Enable floating filters by default
      resizable: true, // Make columns resizable
      sortable: true
    },
    columnTypes: {
      actions: {
        cellRenderer: ActionsRenderer,
        filter: false,
        filterParams: false,
        suppressColumnsToolPanel: true
      },
      link: {
        cellRenderer: LinkRenderer,
        cellClass: "hyperlinks",
        filterParams: {valueFormatter: linkValueFormatter}
      },
      checkbox: {
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true
      },
      single_checkbox: {
        checkboxSelection: true,
        headerCheckboxSelection: false
      },
      date: {
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: dateComparator,
          browserDatePicker: true
        }
      },
      number: {
        filter: "agNumberColumnFilter",
        comparator: numberComparator,
        filterParams: {comparator: numberComparator}
      },
      text: {
        filter: "agTextColumnFilter",
      },
      editable: {
        editable: true,
        cellEditor: "agTextCellEditor",
        cellStyle: linenBackground()
      },
      editable_with_values: {
        editable: true,
        cellStyle: linenBackground(),
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: true,
        cellEditorParams: {
          searchType: "matchAny",
          allowTyping: true,
          filterList: true,
          highlightMatch: true
        }
      }
    },
    components: {
      LinkRenderer: LinkRenderer
    },
    defaultExcelExportParams: {
      autoConvertFormulas: true,
      processCellCallback: excelExportCallback
    },
    excelStyles: [
      {
        id: "hyperlinks",
        font: {
          underline: "Single",
          color: "#358ccb"
        }
      }
    ],
    rowClassRules: {
      "rag-red": "(data.messages && data.messages.indexOf('Validation failed') !== -1) || (data.status && data.status.indexOf('ERROR') !== -1)"
    },
    statusBar: {
      statusPanels: [
        {statusPanel: "agTotalAndFilteredRowCountComponent"},
        {
          statusPanel: "agAggregationComponent",
          statusPanelParams : {
            // only show count and sum ('min', 'max', 'avg' won't be shown)
            aggFuncs: ["count", "sum"]
          }
        }
      ]
    },
    animateRows: true, // Have rows animate to new positions when sorted
    cellSelection: true,
    suppressColumnVirtualisation: true,
    suppressContextMenu: true,
    context: {
      gridHeight: options.gridHeight || "59vh",
      gridName: options.gridName,
      sizeColumnsToFit: false,
      updatePath: options.context?.updatePath
    },
    autoSizeStrategy: {
      defaultMinWidth: 100,
      type: "fitCellContents"
    },
    getRowId: (params) => `${params.data.id}`,
    // Allow filter options to be fully displayed
    onFilterOpened: (event) => {
      let columnWidth = event.column.actualWidth
      if (columnWidth > event.eGui.clientWidth) {
        event.eGui.style.width = `${columnWidth}px`
      }
    },
    onGridSizeChanged: (event) => {
      if (event.clientWidth > 0 || event.clientHeight > 0) {
        resizeColumns(event.api)
      }
    }
  }

  // Delete these to prevent them from being passed to the grid
  delete options.gridName
  delete options.gridHeight
  delete options.context // Delete this to prevent it overriding the default options

  return Object.assign(defaultOptions, options)
}

window.onBtExport = function(element) {
  const gridName = element.dataset.gridName
  const gridApi = document.getElementsByClassName("ag-grid")[gridName].grid

  const date = new Date()
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const fullDate = `${month}.${day}.${year}`

  gridApi.exportDataAsExcel({ fileName: `${gridName}_${fullDate}.xlsx` })
}

window.clearFilters = function(element) {
  const gridName = element.dataset.gridName
  const gridApi = document.getElementsByClassName("ag-grid")[gridName].grid
  gridApi.setFilterModel(null)
  $(document).trigger("filtersCleared")
}

function linenBackground() {
  return {"background-color": "linen"}
}

function resizeColumns(gridApi) {
  gridApi.autoSizeAllColumns()
}
